/**
 * Einstiegspunkt fuer den JS-Bundle (Vite) -> js/script.js
 *
 * Vendor-Libraries und Theme-Skripte aus src/js/*.js.
 * Tailwind CSS benoetigt kein JavaScript-Bundle.
 */

// Seitenspezifische Module werden nur geladen, wenn ihr stabiler Root-Selektor
// vorhanden ist. Das hält Archivlogik, Formulare und Leaflet aus dem globalen
// Bundle heraus. Vollständige Selektoren bleiben für Tailwind statisch sichtbar.
const loadWhenPresent = (selector, loader) => {
    if (!document.querySelector(selector)) {
        return;
    }

    loader().catch((error) => {
        console.error(`Theme-Modul für ${selector} konnte nicht geladen werden.`, error);
    });
};

loadWhenPresent('[data-school-archive]', () => import('./school-archive.js'));
loadWhenPresent('[data-material-archive]', () => import('./material-archive.js'));
loadWhenPresent('[data-angebot-archive]', () => import('./angebot-archive.js'));
loadWhenPresent('[data-fortbildung-archive]', () => import('./fortbildung-archive.js'));
loadWhenPresent('[data-ondemand-archive]', () => import('./on-demand-archive.js'));
loadWhenPresent('.we-login-card .um.um-login', () => import('./login-form.js'));
loadWhenPresent('.um.um-register', () => import('./um-register.js'));
loadWhenPresent('.we-account-form', () => import('./we-account-form.js'));
loadWhenPresent('.we-video-bg[data-video-provider]', () => import('./video-blur-sync.js'));

// Umfangreiche Formular-Overrides nur auf Seiten mit diesen Formularen laden.
loadWhenPresent('.we-angebot-form, .we-login-card, .um.um-register, .we-account-form', () => import('../css/forms.css'));

// "Zurück"-Block (acf/zurueck): ohne uebergeordnete Seite kann PHP das
// Sprungziel nicht ermitteln -- der Button geht dann per JS einen Schritt im
// Browser-Verlauf zurueck.
document.querySelectorAll('[data-back-button]').forEach((button) => {
    button.addEventListener('click', () => window.history.back());
});

// "Zurück"-Link auf Einzelseiten mit clientseitig gefiltertem Archiv (z. B.
// Lernmaterial): fixer Link zeigt sonst immer auf die ungefilterte Archiv-
// Basis-URL. Kommt der Besuch laut document.referrer tatsaechlich von genau
// diesem Archiv (das seine Filter-Auswahl per pushState in die URL schreibt,
// siehe material-archive.js), geht "Zurück" stattdessen per history.back()
// dorthin -- Filter/Scroll-Position bleiben erhalten. Ohne passenden Referrer
// (Direktaufruf, neuer Tab, andere Herkunft) bleibt der normale Link-Fallback
// aktiv.
document.querySelectorAll('[data-smart-back]').forEach((link) => {
    const archivePath = link.dataset.smartBack;
    if (!archivePath || !document.referrer) {
        return;
    }
    let referrerPath;
    try {
        referrerPath = new URL(document.referrer).pathname;
    } catch {
        return;
    }
    if (referrerPath.startsWith(archivePath)) {
        link.addEventListener('click', (event) => {
            event.preventDefault();
            window.history.back();
        });
    }
});

// Sticky Header: position:fixed nur im gescrollten Zustand (.is-scrolled in
// style.css -- position:sticky bricht an body { overflow-x:hidden }, siehe
// dortiger Kommentar). --we-header-height kompensiert den Sprung, wenn der
// Header aus dem Fluss genommen wird; vor jedem Scroll-Start neu gemessen
// (Resize-Handler), damit die Variable immer die natuerliche Hoehe traegt,
// nicht die bereits fixierte. Eigene, vom Menue-Toggle unabhaengige
// Schleife, damit sie auch ohne Mobilmenue-Panel laeuft.
document.querySelectorAll('[data-header]').forEach((header) => {
    const syncHeaderHeight = () => {
        document.documentElement.style.setProperty('--we-header-height', `${header.offsetHeight}px`);
    };
    syncHeaderHeight();
    window.addEventListener('resize', syncHeaderHeight);

    const updateScrolled = () => header.classList.toggle('is-scrolled', window.scrollY > 8);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
});

document.querySelectorAll('[data-header]').forEach((header) => {
    const toggle = header.querySelector('[data-menu-toggle]');
    const panel = header.querySelector('[data-menu-panel]');
    const close = header.querySelector('[data-menu-close]');

    if (!toggle || !panel) {
        return;
    }

    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let previouslyFocusedElement = null;

    const setOpen = (isOpen, restoreFocus = true) => {
        const wasOpen = !panel.classList.contains('hidden');
        panel.classList.toggle('hidden', !isOpen);
        panel.setAttribute('aria-hidden', String(!isOpen));
        toggle.setAttribute('aria-expanded', String(isOpen));
        toggle.querySelector('.sr-only').textContent = isOpen ? 'Menü schließen' : 'Menü öffnen';
        toggle.querySelector('[data-menu-line="first"]').classList.toggle('translate-y-[7px]', isOpen);
        toggle.querySelector('[data-menu-line="first"]').classList.toggle('rotate-45', isOpen);
        toggle.querySelector('[data-menu-line="middle"]').classList.toggle('opacity-0', isOpen);
        toggle.querySelector('[data-menu-line="last"]').classList.toggle('-translate-y-[7px]', isOpen);
        toggle.querySelector('[data-menu-line="last"]').classList.toggle('-rotate-45', isOpen);
        // Vollbild-Overlay: Seite dahinter nicht mitscrollen lassen.
        document.body.classList.toggle('overflow-hidden', isOpen);

        if (isOpen) {
            previouslyFocusedElement = document.activeElement;
            requestAnimationFrame(() => close?.focus());
        } else if (wasOpen && restoreFocus && previouslyFocusedElement instanceof HTMLElement) {
            previouslyFocusedElement.focus();
        }
    };

    toggle.addEventListener('click', () => setOpen(panel.classList.contains('hidden')));
    close?.addEventListener('click', () => setOpen(false));
    panel.addEventListener('click', (event) => {
        if (event.target.closest('a')) {
            setOpen(false, false);
        }
    });
    panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
            return;
        }

        if (event.key !== 'Tab') {
            return;
        }

        const focusableElements = Array.from(panel.querySelectorAll(focusableSelector)).filter((element) => element.getClientRects().length > 0);
        if (!focusableElements.length) {
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
        }
    });

    const desktopQuery = window.matchMedia('(min-width: 901px)');
    desktopQuery.addEventListener('change', (event) => {
        if (event.matches) {
            setOpen(false, false);
        }
    });
});

// Konto-Dropdown im Topmenü (eingeloggt): Vorname-Button oeffnet Profil/Logout.
document.querySelectorAll('[data-account-menu]').forEach((menu) => {
    const toggle = menu.querySelector('[data-account-toggle]');
    const panel = menu.querySelector('[data-account-panel]');
    if (!toggle || !panel) {
        return;
    }

    const setOpen = (isOpen) => {
        panel.classList.toggle('hidden', !isOpen);
        panel.setAttribute('aria-hidden', String(!isOpen));
        toggle.setAttribute('aria-expanded', String(isOpen));
    };

    toggle.addEventListener('click', (event) => {
        event.stopPropagation();
        setOpen(panel.classList.contains('hidden'));
    });
    document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) {
            setOpen(false);
        }
    });
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
            toggle.focus();
        }
    });
});

// Generischer Slider-Verbund: acf/section-header (data-slider-prev/-next) steuert
// einen beliebigen acf/beitrag-slider, acf/angebote-slider bzw. acf/karten-auswahl
// (data-slider) mit gleicher "Slider-Gruppe" — Bloecke sind Geschwister im Content,
// kein gemeinsamer Wrapper. "Das koennte dich interessieren" (single-material.twig)
// traegt seine Pfeile fest verdrahtet im selben Markup, nutzt aber denselben Verbund.
document.querySelectorAll('[data-slider]').forEach((slider) => {
    const group = slider.dataset.slider;
    const previous = document.querySelector(`[data-slider-prev="${group}"]`);
    const next = document.querySelector(`[data-slider-next="${group}"]`);

    if (!previous || !next) {
        return;
    }

    // slider-show="1" ("Im Fokus"): gestapelte Karten (Figma) statt Zeilen-Slider —
    // eigener Karussell-Handler statt Glider, siehe initStackSlider() unten.
    if (slider.dataset.sliderShow === '1') {
        initStackSlider(slider, previous, next);
        return;
    }

    // Natives Scrollen statt Glider.js: Glider berechnet die Kartenbreite selbst
    // aus containerWidth/slidesToShow — bei einem Vollbild-Ausbruch (Karten
    // sollen laut Figma bis zum Bildschirmrand reichen statt im Container
    // gefangen zu sein) waere die gemessene Container-Breite ploetzlich riesig
    // und jede Karte entsprechend ueberbreit (genau das war zuvor der Grund,
    // den Ausbruch wieder zurueckzunehmen). Mit fixer CSS-Kartenbreite
    // (blocks.css) reicht ein simpler scrollBy() pro Pfeilklick, unabhaengig
    // von der Container-/Viewport-Breite; behavior:'smooth' respektiert
    // automatisch prefers-reduced-motion.
    const scrollByCard = (direction) => {
        const card = slider.firstElementChild;
        if (!card) {
            return;
        }
        const gap = parseFloat(getComputedStyle(slider).columnGap) || 0;
        slider.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
    };

    previous.addEventListener('click', () => scrollByCard(-1));
    next.addEventListener('click', () => scrollByCard(1));
});

/**
 * "Im Fokus"-Kartenstapel (Figma "Frame 321" 573:15291 u. a.): alle Karten
 * liegen absolut uebereinander, jede bekommt per data-stack-depth (0 = aktiv,
 * 1/2 = leicht versetzt+ausgeblendet dahinter, "hidden" = unsichtbar) ihre
 * Transform-Stufe aus blocks.css. Pfeile verschieben den Aktiv-Index zyklisch.
 * Die gerade verlassene Karte bekommt statt "hidden" den Sonderstatus "out"
 * (+ data-stack-dir), damit sie sichtbar nach links (weiter) bzw. rechts
 * (zurueck) wegschiebt, statt wie eine normale Stapel-Karte nur zu verblassen.
 */
function initStackSlider(track, previous, next) {
    const cards = Array.from(track.children).filter((el) => el.classList.contains('we-block-slider__card'));
    // Drehrichtung der Stapel-Karten zufaellig nach oben/unten statt immer gleich (blocks.css
    // liest --we-rot-sign in rotate(calc(var(--we-rot-sign, 1) * Xdeg))), einmalig pro Karte.
    cards.forEach((card) => card.style.setProperty('--we-rot-sign', Math.random() < 0.5 ? '-1' : '1'));
    if (cards.length < 2) {
        cards.forEach((card) => card.setAttribute('data-stack-depth', '0'));
        previous.setAttribute('aria-disabled', 'true');
        next.setAttribute('aria-disabled', 'true');
        return;
    }

    let current = 0;
    let autoplayTimer = null;
    const autoplaySeconds = Number.parseFloat(track.dataset.sliderAutoplayInterval || '5');
    const autoplayDelay = Math.max(1, Number.isFinite(autoplaySeconds) ? autoplaySeconds : 5) * 1000;
    const autoplayEnabled = track.dataset.sliderAutoplay === 'true'
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const resize = () => {
        const front = cards[current];
        track.style.height = `${front.offsetHeight}px`;
    };

    const render = (justLeftIndex, direction) => {
        cards.forEach((card, i) => {
            if (i === justLeftIndex && justLeftIndex !== current) {
                card.setAttribute('data-stack-depth', 'out');
                card.setAttribute('data-stack-dir', direction > 0 ? 'left' : 'right');
                card.setAttribute('aria-hidden', 'true');
                return;
            }
            const depth = (i - current + cards.length) % cards.length;
            card.setAttribute('data-stack-depth', depth <= 2 ? String(depth) : 'hidden');
            card.removeAttribute('data-stack-dir');
            card.setAttribute('aria-hidden', depth === 0 ? 'false' : 'true');
        });
        resize();
    };

    const go = (delta) => {
        const justLeftIndex = current;
        current = (current + delta + cards.length) % cards.length;
        render(justLeftIndex, delta);
    };

    const stopAutoplay = () => {
        window.clearTimeout(autoplayTimer);
        autoplayTimer = null;
    };

    const startAutoplay = () => {
        stopAutoplay();
        if (!autoplayEnabled || document.hidden || track.matches(':hover') || track.contains(document.activeElement)) {
            return;
        }
        autoplayTimer = window.setTimeout(() => {
            go(1);
            startAutoplay();
        }, autoplayDelay);
    };

    const manuallyGo = (delta) => {
        go(delta);
        startAutoplay();
    };

    previous.addEventListener('click', () => manuallyGo(-1));
    next.addEventListener('click', () => manuallyGo(1));

    track.addEventListener('mouseenter', stopAutoplay);
    track.addEventListener('mouseleave', startAutoplay);
    track.addEventListener('focusin', stopAutoplay);
    track.addEventListener('focusout', (event) => {
        if (!track.contains(event.relatedTarget)) {
            startAutoplay();
        }
    });
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            stopAutoplay();
        } else {
            startAutoplay();
        }
    });

    // Karten liegen position:absolute uebereinander -- kein Scroll-Container,
    // also kein natives Wisch-Verhalten. Pointer Events (deckt Touch + Maus +
    // Stift einheitlich ab) werten die Wischdistanz erst bei pointerup aus,
    // analog zu den Pfeilen: nach links = vorwaerts (go(1)), nach rechts =
    // zurueck (go(-1)). Kurze/eher vertikale Bewegungen (Seiten-Scroll) loesen
    // nichts aus.
    const swipeThreshold = 40;
    let pointerId = null;
    let pointerStartX = null;
    let pointerStartY = null;

    track.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) {
            return;
        }
        stopAutoplay();
        pointerId = event.pointerId;
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
    });

    const resetPointer = () => {
        pointerId = null;
        pointerStartX = null;
        pointerStartY = null;
    };

    track.addEventListener('pointerup', (event) => {
        if (event.pointerId !== pointerId || pointerStartX === null) {
            return;
        }
        const deltaX = event.clientX - pointerStartX;
        const deltaY = event.clientY - pointerStartY;
        resetPointer();
        if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) < Math.abs(deltaY)) {
            startAutoplay();
            return;
        }
        manuallyGo(deltaX < 0 ? 1 : -1);
    });
    track.addEventListener('pointercancel', () => {
        resetPointer();
        startAutoplay();
    });

    window.addEventListener('resize', resize);
    // Kartenhoehe ist textabhaengig (Outfit laedt als lokales woff2 und tauscht
    // ggf. erst nach dem ersten render() ein) — nach dem Font-Swap neu messen,
    // sonst bleibt die Track-Hoehe auf den Fallback-Font-Metriken stehen.
    document.fonts?.ready?.then(resize);

    track.classList.add('is-stacked');
    render();
    startAutoplay();
}

document.querySelectorAll('[data-material-bookmark], [data-bookmark]').forEach((form) => {
    const button = form.querySelector('[data-bookmark-button]');
    const label = form.querySelector('[data-bookmark-label]');
    const status = form.querySelector('[data-bookmark-status]');
    const itemLabel = form.dataset.bookmarkItemLabel || 'Das Lernmaterial';

    if (!button || !label || !status || typeof window.fetch !== 'function') {
        return;
    }

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        button.disabled = true;
        status.textContent = '';

        const formData = new FormData(form);
        const endpoint = form.getAttribute('action');
        formData.append('response', 'json');

        try {
            if (!endpoint) {
                throw new Error('bookmark_endpoint_missing');
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error('bookmark_failed');
            }

            const isBookmarked = Boolean(result.data.is_bookmarked);
            button.setAttribute('aria-pressed', String(isBookmarked));
            label.textContent = result.data.label;
            status.textContent = isBookmarked ? `${itemLabel} wurde gespeichert.` : `${itemLabel} wurde entfernt.`;
        } catch (error) {
            status.textContent = 'Die Merkliste konnte nicht aktualisiert werden. Bitte versuche es erneut.';
        } finally {
            button.disabled = false;
        }
    });
});

document.querySelectorAll('[data-material-more]').forEach((wrapper) => {
    const toggle = wrapper.querySelector('[data-material-more-toggle]');
    const content = wrapper.querySelector('[data-material-more-content]');

    if (!toggle || !content) {
        return;
    }

    toggle.addEventListener('click', () => {
        content.classList.remove('hidden');
        toggle.remove();
    });
});

// Registrieren-CTA (Startseite) verlinkt mit ?email=... auf die UM-Registrierungsseite —
// hier ins UM-E-Mail-Feld uebernehmen (data-key ist stabil, anders als der um id-Suffix).
(() => {
    const email = new URLSearchParams(window.location.search).get('email');
    const emailField = document.querySelector('.we-register-card input[data-key="user_email"]');
    if (email && emailField && !emailField.value) {
        emailField.value = email;
    }
})();

document.querySelectorAll('[data-newsletter-form]').forEach((form) => {
    const status = form.querySelector('[data-newsletter-status]');
    const successMessage = form.parentElement?.querySelector('[data-newsletter-success]');

    if (!status || typeof window.fetch !== 'function') {
        return;
    }

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        if (button) {
            button.disabled = true;
        }
        status.hidden = true;
        status.textContent = '';
        status.classList.remove('we-block-newsletter__status--error');

        const formData = new FormData(form);
        formData.append('response', 'json');

        try {
            // form.action liefert hier faelschlich das <input name="action">-Feld statt der
            // URL zurueck (DOM-Clobbering: WordPress braucht ein POST-Feld exakt "action" fuer
            // admin-post.php, das ueberschreibt die gleichnamige Formular-Eigenschaft) -
            // getAttribute('action') ist davon nicht betroffen.
            const response = await fetch(form.getAttribute('action'), {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
                headers: { Accept: 'application/json' },
            });
            const result = await response.json();
            if (!response.ok || !result.success) {
                throw new Error(result?.data?.message || 'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.');
            }

            if (successMessage) {
                form.hidden = true;
                successMessage.hidden = false;
            } else {
                status.textContent = 'Danke! Bitte bestätige deine Anmeldung über den Link in der E-Mail, die wir dir gerade gesendet haben.';
                status.hidden = false;
            }
        } catch (error) {
            status.textContent = error.message || 'Die Anmeldung ist fehlgeschlagen. Bitte versuche es erneut.';
            status.classList.add('we-block-newsletter__status--error');
            status.hidden = false;
        } finally {
            if (button) {
                button.disabled = false;
            }
        }
    });
});

document.querySelectorAll('[data-praxis-image]').forEach((imageSection) => {
	const dialog = imageSection.querySelector('[data-praxis-image-dialog]');
	const openButton = imageSection.querySelector('[data-praxis-image-open]');
	const closeButton = imageSection.querySelector('[data-praxis-image-close]');

	if (!dialog || !openButton || !closeButton || typeof dialog.showModal !== 'function') {
		return;
	}

	openButton.addEventListener('click', () => dialog.showModal());
	closeButton.addEventListener('click', () => dialog.close());
	dialog.addEventListener('click', (event) => {
		if (event.target === dialog) {
			dialog.close();
		}
	});
});
